<!--
// app/pages/[region]/matches/[matchId]-[homeTeam]-vs-[awayTeam].vue
-->
<script setup lang="ts">
import type { Match } from "~/src/match/domain/models/Match";
import { useMatches } from "~/app/composables/season/matches";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useWebContext } from "~/app/composables/webContext";
import matchService, { $MATCH_REFRESH_INTERVAL } from "~/src/match/domain/services/matchService";
import GridPartnersCompetition from "~/app/components/Shared/GridPartnersCompetition.vue";
import CurrentSeasonSliderMatches from "~/app/components/Shared/slider-matches/CurrentSeasonSliderMatches.vue";
import MatchMainJumbotron from "~/app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchMainJumbotron.vue";
import MatchStatsContainer from "~/app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchStatsContainer.vue";
import { isFutureDate, isPastDate } from "~/src/Shared/utils";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/matches/[matchId]-[homeTeam]-vs-[awayTeam]",
    es: "/[region]/partidos/[matchId]-[homeTeam]-vs-[awayTeam]",
    de: "/[region]/spiele/[matchId]-[homeTeam]-vs-[awayTeam]",
    fr: "/[region]/matchs/[matchId]-[homeTeam]-vs-[awayTeam]",
    it: "/[region]/partite/[matchId]-[homeTeam]-vs-[awayTeam]",
    pt: "/[region]/jogos/[matchId]-[homeTeam]-vs-[awayTeam]",
    ar: "/[region]/matches/[matchId]-[homeTeam]-vs-[awayTeam]",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

const { useFetchParamsMatchData } = useMatches();
const { trackLiveMatch, matchIsLive } = matchService();
const { region } = useCompetitionStore();
const { webContextGenders } = useWebContext();
const route = useRoute();
const match = ref<Match>();
const intervalId = ref<NodeJS.Timeout>();

const matchId = typeof route.params.matchId === "string" && Number(route.params.matchId);

const clearMatchInterval = () => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
    intervalId.value = undefined;
  }
};

const getMatchData = async (matchId: number, live = false): Promise<void> => {
  const fetchPath = useFetchParamsMatchData(matchId, live).path;
  const fetchOpts = useFetchParamsMatchData(matchId, live).opts;

  const { data } = await useFetch(fetchPath, fetchOpts);
  const _match = data.value ? data.value : undefined;

  if (_match) {
    match.value = _match;
  }
};

if (matchId) {
  const fetchPath = useFetchParamsMatchData(matchId, false).path;
  const fetchOpts = useFetchParamsMatchData(matchId, false).opts;
  const { data, error } = await useFetch(fetchPath, fetchOpts);
  if (error.value && error.value.statusCode === 404) {
    throw createError({ statusCode: 404 });
  } else if (error.value) {
    console.error(error.value);
  }
  const _match: Match | undefined = data.value ? data.value : undefined;

  if (_match) {
    const matchDateMargin = new Date(_match.date);
    matchDateMargin.setHours(matchDateMargin.getHours() + 2); // sum 3 hours to the start date as margin
    const shouldCheckLive = isPastDate(_match.date) && isFutureDate(matchDateMargin.toString());

    if (matchIsLive(_match) || shouldCheckLive) {
      await getMatchData(matchId, true);
    } else {
      match.value = _match;
    }
  }
}

onMounted(() => {
  if (matchId && match.value && trackLiveMatch(match.value)) {
    intervalId.value = setInterval(async function () {
      await getMatchData(matchId, true);
      if (match.value && !matchIsLive(match.value)) {
        clearMatchInterval();
      }
    }, $MATCH_REFRESH_INTERVAL);
  }
});

onUnmounted(() => {
  clearMatchInterval();
});

useSeoMeta({
  title: () =>
    match.value
      ? `${match.value?.participants.homeTeam?.completeName} vs ${match.value?.participants.awayTeam?.completeName}`
      : null,
  ogTitle: () =>
    match.value
      ? `${match.value?.participants.homeTeam?.completeName} vs ${match.value?.participants.awayTeam?.completeName}`
      : null,
});

useServerSeoMeta({
  robots: "noindex, follow",
});
</script>

<template>
  <match-main-jumbotron v-if="!!match" :match="match"></match-main-jumbotron>
  <div class="kql-w-container">
    <match-stats-container v-if="!!match" :match="match"></match-stats-container>
  </div>
  <div class="relative pb-8">
    <current-season-slider-matches
      section-title
      with-add-to-calendar-in-section
    ></current-season-slider-matches>
  </div>
  <grid-partners-competition></grid-partners-competition>
</template>

<style scoped></style>
